import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import LoadBalanceModal from "@/components/business/pos/LoadBalanceModal";
import {
  Users,
  ShoppingCart,
  History,
  Plus,
  Eye,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Search,
  UserPlus,

  RefreshCw,


  Sparkles,
  Heart,
  Gift,
  Star,
} from "lucide-react";
import { OrderHistoryWithAssignments } from "@/components/business/orders/OrderHistoryWithAssignments";
import { ChildCard } from "@/components/business/family/ChildrenManagement/ChildCard";
import { AddChildModal } from "@/components/business/family/ChildrenManagement/AddChildModal";
import { AssignTicketsModal } from "@/components/business/family/ChildrenManagement/AssignTicketsModal";

import { familyService, type Student, type CreateStudentRequest } from "@/services";
import { useToast } from "@/hooks/useToast";
import { useAuth } from "@/hooks/useAuth";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useUserDashboardStats } from "@/hooks/useDashboardData";

/**
 * Interfaz para los datos de los hijos (mantener compatibilidad)
 */
interface IHijo {
  id: string;
  nombres: string;
  apellidos: string;
  saldo: number;
}

/**
 * Formatea el saldo como moneda chilena
 */
// const formatSaldo = (saldo: number): string => {
//   return new Intl.NumberFormat("es-CL", {
//     style: "currency",
//     currency: "CLP",
//     minimumFractionDigits: 0,
//     maximumFractionDigits: 0,
//   }).format(saldo);
// };

/**
 * Obtiene el estado del saldo y sus colores correspondientes
 */
const getSaldoStatus = (saldo: number) => {
  if (saldo > 10000) {
    return {
      status: "Alto",
      color: "text-emerald-600",
      bgColor: "bg-emerald-500/10",
      borderColor: "border-emerald-500/20",
      icon: CheckCircle,
      gradient: "from-emerald-500/20 to-emerald-600/10",
    };
  } else if (saldo > 5000) {
    return {
      status: "Medio",
      color: "text-amber-600",
      bgColor: "bg-amber-500/10",
      borderColor: "border-amber-500/20",
      icon: TrendingUp,
      gradient: "from-amber-500/20 to-amber-600/10",
    };
  } else {
    return {
      status: "Bajo",
      color: "text-rose-600",
      bgColor: "bg-rose-500/10",
      borderColor: "border-rose-500/20",
      icon: AlertCircle,
      gradient: "from-rose-500/20 to-rose-600/10",
    };
  }
};

/**
 * Página del Dashboard del Apoderado
 *
 * Proporciona una interfaz completa para que los apoderados
 * gestionen la información de sus hijos y realicen acciones
 * relacionadas con sus cuentas.
 * Optimizada para dispositivos móviles con layout responsive.
 */
const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user } = useAuth();

  // Estados para el modal de carga de saldo
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState<IHijo | null>(null);
  const [activeTab, setActiveTab] = useState("gestion-hijos");

  // Estados para gestión de hijos conectada al backend
  const [students, setStudents] = useState<Student[]>([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [errorStudents, setErrorStudents] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [availablePacksToAssign, setAvailablePacksToAssign] = useState<number | null>(null);

  // Estados para modales
  const [isAddChildModalOpen, setIsAddChildModalOpen] = useState(false);
  const [isAssignTicketsModalOpen, setIsAssignTicketsModalOpen] =
    useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);

  // Debounce del searchQuery para evitar requests en cada tecla
  const debouncedSearchQuery = useDebounceValue(searchQuery, 500);

  // React Query para stats del dashboard (con caché de 3 minutos)
  // TODO: Usar dashboardStats para reemplazar valores hard-coded en el hero
  const {
    data: _dashboardStats,
    isLoading: _isLoadingDashboardStats,
  } = useUserDashboardStats();

  // Efecto para manejar el parámetro tab en la URL
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (
      tabParam &&
      ["gestion-hijos", "tienda", "listas-compra"].includes(tabParam)
    ) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Cargar estudiantes cuando se monta el componente o cambia la búsqueda (con debounce)
  useEffect(() => {
    if (activeTab === "gestion-hijos") {
      loadStudents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, debouncedSearchQuery]);

  /**
   * Carga los estudiantes vinculados al apoderado
   */
  const loadStudents = async () => {
    if (!user?.id) {
      setErrorStudents("Usuario no autenticado");
      return;
    }

    setIsLoadingStudents(true);
    setErrorStudents(null);

    try {
      console.log('🔍 [FRONTEND] Cargando estudiantes...', {
        userId: user.id,
        searchQuery: debouncedSearchQuery
      });
      const response = await familyService.getLinkedStudents(debouncedSearchQuery);
      console.log('📊 [FRONTEND] Respuesta recibida:', {
        availablePacksToAssign: response.availablePacksToAssign,
        linkedStudentsCount: response.linkedStudents.length,
        linkedStudents: response.linkedStudents
      });
      setStudents(response.linkedStudents);
      setAvailablePacksToAssign(response.availablePacksToAssign);
      console.log('✅ [FRONTEND] Estado actualizado:', {
        availablePacksToAssign: response.availablePacksToAssign
      });
    } catch (error: any) {
      setErrorStudents("Error al cargar los estudiantes");
      console.error("Error loading students:", error);
    } finally {
      setIsLoadingStudents(false);
    }
  };

  /**
   * Maneja el registro de un nuevo estudiante
   */
  const handleAddStudent = async (studentData: CreateStudentRequest) => {
    if (!user?.id) {
      toast({
        title: "Error",
        description: "Usuario no autenticado",
        variant: "destructive",
      });
      return;
    }

    try {
      await familyService.registerAndLinkStudent(studentData);
      toast({
        title: "Estudiante registrado",
        description: `${studentData.nombre} ${studentData.apellido} ha sido registrado exitosamente`,
      });
      loadStudents(); // Recargar lista
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al registrar estudiante",
        variant: "destructive",
      });
      throw error; // Re-lanzar para que el modal maneje el error
    }
  };

  /**
   * Maneja la asignación de productos
   */
  const handleAssignTickets = () => {
    loadStudents(); // Recargar lista después de asignar
    toast({
      title: "Productos asignados",
      description: "Los productos han sido asignados exitosamente",
    });
  };

  /**
   * Maneja la acción de asignar productos para estudiantes reales
   */
  const handleCargarSaldoStudent = (student: Student) => {
    setSelectedStudent(student);
    setIsAssignTicketsModalOpen(true);
  };

  /**
   * Maneja la acción de ver consumos para estudiantes reales
   */
  const handleVerConsumosStudent = (student: Student) => {
    navigate(`/dashboard/history/${student.id}`);
  };

  /**
   * Maneja la acción de ver consumos V2 para estudiantes reales
   * DEPRECATED: Ahora se usa la ruta principal
   */
  // const handleVerConsumosStudentV2 = (student: Student) => {
  //   navigate(`/dashboard/history-v2/${student.id}`);
  // };

  /**
   * Maneja el cierre del modal
   */
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedChild(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="container mx-auto p-3 sm:p-6 max-w-6xl">
        {/* Hero Section Mejorado - Mobile Optimized */}
        <div className="relative mb-6 sm:mb-8">
          {/* Fondo con gradiente y efectos */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-2xl sm:rounded-3xl blur-2xl sm:blur-3xl"></div>

          {/* Contenido del Hero */}
          <div className="relative bg-gradient-to-r from-card/80 to-card/60 backdrop-blur-xl border border-border/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl sm:shadow-2xl">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary to-primary/80 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold text-foreground mb-1 sm:mb-2 leading-tight">
                  Panel del Apoderado
                </h1>
                <p className="text-xs sm:text-sm lg:text-base text-muted-foreground leading-relaxed">
                  Gestiona la información de tus hijos y sus cuentas con amor y
                  cuidado
                </p>
              </div>
            </div>

            {/* Estadísticas rápidas - Mobile Optimized */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div className="bg-gradient-to-r from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                  <Users className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" />
                  <span className="text-xs sm:text-sm font-medium text-emerald-600 truncate">
                    Hijos
                  </span>
                </div>
                <span className="text-lg sm:text-xl lg:text-2xl font-bold text-emerald-600">
                  {students.length}
                </span>
              </div>

              <div className="bg-gradient-to-r from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                  <Gift className="w-3 h-3 sm:w-4 sm:h-4 text-amber-600" />
                  <span className="text-xs sm:text-sm font-medium text-amber-600 truncate">
                    Packs
                  </span>
                </div>
                <span className="text-lg sm:text-xl lg:text-2xl font-bold text-amber-600">
                  {availablePacksToAssign !== null ? availablePacksToAssign : '...'}
                </span>
                {/* DEBUG: availablePacksToAssign = {availablePacksToAssign} */}
              </div>

              <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                  <Star className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                  <span className="text-xs sm:text-sm font-medium text-primary truncate">
                    Estado
                  </span>
                </div>
                <span className="text-lg sm:text-xl lg:text-2xl font-bold text-primary">
                  Excelente
                </span>
              </div>

              <div className="bg-gradient-to-r from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                  <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                  <span className="text-xs sm:text-sm font-medium text-blue-600 truncate">
                    Activo
                  </span>
                </div>
                <span className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-600">
                  100%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Sistema de pestañas mejorado - Mobile Optimized */}
        <div className="space-y-6 sm:space-y-8">
          {/* Indicador de navegación */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              <span className="text-xs sm:text-sm text-muted-foreground font-medium">
                Navegación del Panel
              </span>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span>Sección activa:</span>
              <span className="font-medium text-primary">
                {activeTab === "gestion-hijos" && "Gestión de Hijos"}
                {activeTab === "tienda" && "Tienda Online"}
                {activeTab === "listas-compra" && "Historial de Compras"}
                {/* {activeTab === "historial-compras" && "Gestión Avanzada"} */}
              </span>
            </div>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="space-y-6 sm:space-y-8"
          >
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-3 bg-gradient-to-r from-card/80 to-card/60 backdrop-blur-xl border border-border/50 rounded-xl sm:rounded-2xl p-1.5 shadow-lg min-h-[3.5rem] sm:min-h-[4rem]">
              <TabsTrigger
                value="gestion-hijos"
                className="flex items-center justify-center gap-1 sm:gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-primary/80 data-[state=active]:text-primary-foreground data-[state=active]:shadow-lg rounded-lg sm:rounded-xl transition-all duration-300 text-xs sm:text-sm font-medium h-12 sm:h-14 hover:bg-primary/10 hover:scale-105 active:scale-95"
              >
                <Users className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="hidden sm:inline">Gestión de Hijos</span>
                <span className="sm:hidden">Hijos</span>
              </TabsTrigger>
              <TabsTrigger
                value="tienda"
                className="flex items-center justify-center gap-1 sm:gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-primary/80 data-[state=active]:text-primary-foreground data-[state=active]:shadow-lg rounded-lg sm:rounded-xl transition-all duration-300 text-xs sm:text-sm font-medium h-12 sm:h-14 hover:text-yellow-500 hover:scale-105 active:scale-95"
              >
                <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="hidden sm:inline">Tienda Online</span>
                <span className="sm:hidden">Tienda</span>
              </TabsTrigger>
              <TabsTrigger
                value="listas-compra"
                className="flex items-center justify-center gap-1 sm:gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-primary/80 data-[state=active]:text-primary-foreground data-[state=active]:shadow-lg rounded-lg sm:rounded-xl transition-all duration-300 text-xs sm:text-sm font-medium h-12 sm:h-14 hover:bg-primary/10 hover:scale-105 active:scale-95"
              >
                <History className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="hidden sm:inline">Historial de Compras</span>
                <span className="sm:hidden">Historial</span>
              </TabsTrigger>
              {/* TabsTrigger comentado para "Gestión Avanzada" - LEGACY CODE
              <TabsTrigger
                value="historial-compras"
                className="flex items-center justify-center gap-1 sm:gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary data-[state=active]:to-primary/80 data-[state=active]:text-primary-foreground data-[state=active]:shadow-lg rounded-lg sm:rounded-xl transition-all duration-300 text-xs sm:text-sm font-medium h-12 sm:h-14 hover:bg-primary/10 hover:scale-105 active:scale-95"
              >
                <Package className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="hidden sm:inline">Gestión Avanzada</span>
                <span className="sm:hidden">Avanzada</span>
              </TabsTrigger>
              */}
            </TabsList>

            {/* Pestaña: Gestión de Hijos Mejorada - Mobile Optimized */}
            <TabsContent
              value="gestion-hijos"
              className="space-y-6 sm:space-y-6 pt-8 sm:pt-4 lg:pt-2"
            >
              <Card className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl border border-border/50 rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl hover:shadow-2xl sm:hover:shadow-3xl transition-all duration-300 group">
                <CardHeader className="pb-4 sm:pb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-primary/80 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                        <Users className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground">
                          Hijos Registrados
                        </CardTitle>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          Gestiona las cuentas de tus hijos y asigna productos
                          disponibles
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={loadStudents}
                        disabled={isLoadingStudents}
                        className="h-8 sm:h-9 hover:bg-primary/10 transition-colors duration-200"
                      >
                        <RefreshCw
                          className={`h-3 w-3 sm:h-4 sm:w-4 ${
                            isLoadingStudents ? "animate-spin" : ""
                          }`}
                        />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setIsAddChildModalOpen(true)}
                        className="h-8 sm:h-9 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-200 shadow-lg hover:shadow-xl text-xs sm:text-sm"
                      >
                        <UserPlus className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        <span className="hidden sm:inline">Agregar Hijo</span>
                        <span className="sm:hidden">Agregar</span>
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 sm:space-y-6">
                  {/* Barra de búsqueda mejorada - Mobile Optimized */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                    </div>
                    <Input
                      placeholder="Buscar estudiantes..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-10 sm:h-12 bg-background/50 border-border/50 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 rounded-xl sm:rounded-2xl transition-all duration-200 text-sm sm:text-base"
                    />
                  </div>

                  {/* Información de productos disponibles mejorada - Mobile Optimized */}
                  {availablePacksToAssign !== null && availablePacksToAssign > 0 && (
                    <div className="bg-gradient-to-r from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Gift className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-emerald-600">
                          Packs disponibles para asignar
                        </p>
                        <p className="text-sm sm:text-lg font-bold text-emerald-600">
                          {availablePacksToAssign} packs
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* DEBUG: Mostrar cuando no hay packs */}
                  {availablePacksToAssign !== null && availablePacksToAssign === 0 && (
                    <div className="bg-gradient-to-r from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-amber-600">
                          No hay packs disponibles
                        </p>
                        <p className="text-sm sm:text-lg font-bold text-amber-600">
                          {availablePacksToAssign} packs
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Estado de carga mejorado - Mobile Optimized */}
                  {isLoadingStudents && (
                    <div className="flex items-center justify-center py-8 sm:py-12">
                      <div className="text-center">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3 sm:mb-4"></div>
                        <p className="text-sm sm:text-base text-muted-foreground font-medium">
                          Cargando estudiantes...
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Estado de error mejorado - Mobile Optimized */}
                  {errorStudents && (
                    <Alert
                      variant="destructive"
                      className="border-red-500/20 bg-red-500/10 rounded-xl sm:rounded-2xl"
                    >
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="text-sm">
                        {errorStudents}
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Lista vacía mejorada - Mobile Optimized */}
                  {!isLoadingStudents &&
                    !errorStudents &&
                    students.length === 0 && (
                      <div className="text-center py-8 sm:py-12">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                          <Users className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
                        </div>
                        <h3 className="text-base sm:text-lg font-medium text-foreground mb-2">
                          No hay estudiantes registrados
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4 sm:mb-6">
                          Agrega tu primer hijo para comenzar a gestionar sus
                          productos
                        </p>
                        <Button
                          onClick={() => setIsAddChildModalOpen(true)}
                          className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-200 shadow-lg hover:shadow-xl text-sm sm:text-base"
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          Agregar Primer Hijo
                        </Button>
                      </div>
                    )}

                  {/* Vista Desktop - Tabla mejorada */}
                  {!isLoadingStudents &&
                    !errorStudents &&
                    students.length > 0 && (
                      <div className="hidden lg:block">
                        <div className="bg-gradient-to-r from-card/60 to-card/40 backdrop-blur-xl border border-border/50 rounded-2xl overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-muted/30 hover:bg-muted/50 transition-colors duration-200">
                                <TableHead className="font-semibold text-foreground">
                                  Nombre Completo
                                </TableHead>
                                <TableHead className="font-semibold text-foreground">
                                  Email
                                </TableHead>
                                <TableHead className="font-semibold text-foreground">
                                  Productos Disponibles
                                </TableHead>
                                <TableHead className="text-right font-semibold text-foreground">
                                  Acciones
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {students.map((student) => {
                                const saldoStatus = getSaldoStatus(
                                  student.ticketBalance || 0
                                );
                                const StatusIcon = saldoStatus.icon;

                                return (
                                  <TableRow
                                    key={student.id}
                                    className="hover:bg-muted/30 transition-colors duration-200"
                                  >
                                    <TableCell className="font-medium text-foreground">
                                      {student.nombre} {student.apellido}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                      {student.email}
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex items-center gap-3">
                                        <div
                                          className={`w-8 h-8 ${saldoStatus.bgColor} rounded-full flex items-center justify-center`}
                                        >
                                          <StatusIcon
                                            className={`h-4 w-4 ${saldoStatus.color}`}
                                          />
                                        </div>
                                        <div>
                                          <span
                                            className={`font-bold text-lg ${saldoStatus.color}`}
                                          >
                                            {student.ticketBalance || 0}
                                          </span>
                                          <span className="text-sm text-muted-foreground ml-1">
                                            productos
                                          </span>
                                        </div>
                                        <Badge
                                          variant="outline"
                                          className={`text-xs ${saldoStatus.borderColor}`}
                                        >
                                          {saldoStatus.status}
                                        </Badge>
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <div className="flex justify-end space-x-2">
                                        <Button
                                          onClick={() =>
                                            handleCargarSaldoStudent(student)
                                          }
                                          size="sm"
                                          className="h-8 sm:h-9 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-200 shadow-md hover:shadow-lg text-xs"
                                        >
                                          <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                          Asignar
                                        </Button>
                                        <Button
                                          onClick={() =>
                                            handleVerConsumosStudent(student)
                                          }
                                          variant="outline"
                                          size="sm"
                                          className="h-8 sm:h-9 hover:bg-primary/10 transition-colors duration-200 text-xs"
                                        >
                                          <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                                          Ver Historial
                                        </Button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}

                  {/* Vista Mobile - Cards mejoradas */}
                  {!isLoadingStudents &&
                    !errorStudents &&
                    students.length > 0 && (
                      <div className="lg:hidden space-y-3 sm:space-y-4">
                        {students.map((student) => (
                          <ChildCard
                            key={student.id}
                            student={student}
                            onCargarSaldo={handleCargarSaldoStudent}
                            onVerConsumos={handleVerConsumosStudent}
                          />
                        ))}
                      </div>
                    )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Pestaña: Tienda Online Mejorada - Mobile Optimized */}
            <TabsContent
              value="tienda"
              className="space-y-6 sm:space-y-6 pt-8 sm:pt-4 lg:pt-2"
            >
              <Card className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl border border-border/50 rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl hover:shadow-2xl sm:hover:shadow-3xl transition-all duration-300">
                <CardHeader className="pb-4 sm:pb-6">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-primary/80 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
                      <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground">
                        Tienda Online
                      </CardTitle>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Explora y compra productos para tus hijos
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 sm:py-12">
                    <div className="max-w-sm sm:max-w-md mx-auto">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-primary/20 to-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                        <ShoppingCart className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-foreground">
                        Accede a la Tienda
                      </h3>
                      <p className="text-sm sm:text-base text-muted-foreground mb-6 sm:mb-8 leading-relaxed">
                        Explora nuestro catálogo completo de productos y realiza
                        compras para tus hijos con total seguridad
                      </p>
                      <Button
                        onClick={() => navigate("/store")}
                        className="w-full h-12 sm:h-14 text-sm sm:text-base font-medium bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-200 shadow-lg hover:shadow-xl rounded-xl sm:rounded-2xl"
                      >
                        <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                        Ir a la Tienda
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Pestaña: Historial de Compras y Asignaciones Mejorada - Mobile Optimized */}
            <TabsContent
              value="listas-compra"
              className="space-y-6 sm:space-y-6 pt-8 sm:pt-4 lg:pt-2"
            >
              <Card className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl border border-border/50 rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl hover:shadow-2xl sm:hover:shadow-3xl transition-all duration-300">
                <CardHeader className="pb-4 sm:pb-6">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-primary/80 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
                      <History className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground">
                        Historial de Compras y Asignaciones
                      </CardTitle>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Revisa tus compras y asigna productos a tus hijos
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <OrderHistoryWithAssignments />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Pestaña: Gestión de Listas Mejorada - Mobile Optimized - LEGACY CODE COMENTADO
            <TabsContent
              value="historial-compras"
              className="space-y-6 sm:space-y-6 pt-8 sm:pt-4 lg:pt-2"
            >
              <Card className="bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl border border-border/50 rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl hover:shadow-2xl sm:hover:shadow-3xl transition-all duration-300">
                <CardHeader className="pb-4 sm:pb-6">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-primary/80 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
                      <Package className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground">
                        Gestión de Listas de Compra
                      </CardTitle>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Gestiona listas de compra y asignaciones avanzadas
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <PurchaseListManager />
                </CardContent>
              </Card>
            </TabsContent>
            */}
          </Tabs>
        </div>

        {/* Modal para cargar saldo (compatibilidad con datos mock) */}
        {selectedChild && (
          <LoadBalanceModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            childName={`${selectedChild.nombres} ${selectedChild.apellidos}`}
            childId={selectedChild.id}
          />
        )}

        {/* Modal para agregar nuevo hijo */}
        <AddChildModal
          isOpen={isAddChildModalOpen}
          onClose={() => setIsAddChildModalOpen(false)}
          onSubmit={handleAddStudent}
        />

        {/* Modal para asignar tickets */}
        <AssignTicketsModal
          isOpen={isAssignTicketsModalOpen}
          onClose={() => setIsAssignTicketsModalOpen(false)}
          student={selectedStudent}
          apoderadoId={user?.id || ""}
          onSuccess={handleAssignTickets}
        />
      </div>
    </div>
  );
};

export default DashboardPage;
